import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { SessionBet, useSocket } from "../../providers/SocketProvider";
import {
  loadRoundResults,
  ProvablyFairRoundData,
} from "../../services/roundResultsService";
import ProvablyFairDetails from "./provablyHistoryModal";

const formatPlacedAt = (value: string) => {
  const placedAt = new Date(value);
  if (Number.isNaN(placedAt.getTime())) {
    return { time: "--:--", date: "--/--/--" };
  }

  const day = String(placedAt.getDate()).padStart(2, "0");
  const month = String(placedAt.getMonth() + 1).padStart(2, "0");
  const year = String(placedAt.getFullYear()).slice(-2);

  return {
    time: placedAt.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }),
    date: `${day}-${month}-${year}`,
  };
};

const formatMultiplier = (bet: SessionBet) =>
  typeof bet.multiplier === "number"
    ? `${bet.multiplier.toFixed(2)}x`
    : "—";

const multiplierClass = (multiplier?: number) => {
  if (typeof multiplier !== "number") return "text-[#7b7b7b]";
  if (multiplier < 2) return "text-blue-400";
  if (multiplier < 10) return "text-purple-400";
  return "text-pink-500";
};

const SessionBetHistoryModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) => {
  const { currencyConfig, sessionBetHistory } = useSocket();
  const [visibleCount, setVisibleCount] = useState(10);
  const [selectedRound, setSelectedRound] =
    useState<ProvablyFairRoundData | null>(null);
  const [fairnessLoadingId, setFairnessLoadingId] = useState<string | null>(
    null,
  );
  const [fairnessError, setFairnessError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setVisibleCount(10);
      setFairnessError("");
    } else {
      setSelectedRound(null);
    }
  }, [isOpen]);

  const openProvablyFairDetails = async (bet: SessionBet) => {
    if (bet.status === "placed" || fairnessLoadingId) return;

    setFairnessError("");
    setFairnessLoadingId(bet.sessionId);

    try {
      const rounds = await loadRoundResults();
      const round = rounds.find(
        (item) => String(item.roundId) === String(bet.roundId),
      );

      if (!round) {
        setFairnessError(
          "Provably Fair data is not available for this round yet.",
        );
        return;
      }

      setSelectedRound(round);
    } catch {
      setFairnessError("Unable to load Provably Fair data. Please try again.");
    } finally {
      setFairnessLoadingId(null);
    }
  };

  if (!isOpen) return null;

  const visibleBets = sessionBetHistory.slice(0, visibleCount);

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-start pt-8"
        onClick={onClose}
      >
      <div
        className="bg-[#1b1c1d] w-full max-w-[500px] rounded-xl text-white shadow-xl relative overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex justify-between items-center px-4 py-2 bg-[#242424] border-b border-[#2c2d30]">
          <span className="text-base font-semibold">MY BET HISTORY</span>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-200"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-2 text-[11px] text-[#7b7b7b] font-medium flex justify-between border-b border-[#2c2d30]">
          <div className="w-[30%]">Date</div>
          <div className="w-[38%] text-left">
            Bet {currencyConfig.code} X
          </div>
          <div className="w-[30%] text-right">
            Cash out {currencyConfig.code}
          </div>
        </div>

        <div className="max-h-[360px] overflow-y-auto">
          {visibleBets.length === 0 && (
            <div className="h-28 flex items-center justify-center text-sm text-[#7b7b7b]">
              No bets placed in this session.
            </div>
          )}

          {visibleBets.map((bet, index) => {
            const { time, date } = formatPlacedAt(bet.placedAt);
            const won = bet.status === "cashed_out";

            return (
              <div
                key={bet.sessionId}
                className={`mx-2 py-1 px-2 flex justify-between items-center text-sm ${
                  won ? "bg-green-800 bg-opacity-70" : "bg-[#101112]"
                } ${index !== visibleBets.length - 1 ? "border-b" : ""}`}
                style={{ borderColor: "#2c2d30", borderRadius: "8px" }}
              >
                <div className="w-[30%] leading-tight text-white text-[11px]">
                  <div>{time}</div>
                  <div>{date}</div>
                </div>

                <div className="w-[38%] text-center flex items-center justify-start">
                  <div className="text-[#bbbfc5]">
                    {bet.stake.toFixed(2)}
                  </div>
                  <div
                    className={`text-xs font-semibold px-2 py-0.5 ml-2 rounded-full bg-black bg-opacity-30 ${multiplierClass(
                      bet.multiplier,
                    )}`}
                  >
                    {formatMultiplier(bet)}
                  </div>
                </div>

                <div className="w-[30%] text-right text-white text-[12px]">
                  {won && typeof bet.cashout === "number" ? (
                    <div className="flex justify-end items-center gap-2">
                      <span>{bet.cashout.toFixed(2)}</span>
                      <button
                        type="button"
                        onClick={() => openProvablyFairDetails(bet)}
                        disabled={fairnessLoadingId !== null}
                        className="disabled:opacity-50"
                        aria-label={`Verify round ${bet.roundId}`}
                        title="Verify Provably Fair result"
                      >
                        <img
                          src="/icon-provabyfair.svg"
                          alt=""
                          className={`w-4 h-4 ${
                            fairnessLoadingId === bet.sessionId
                              ? "animate-pulse"
                              : ""
                          }`}
                        />
                      </button>
                    </div>
                  ) : bet.status === "placed" ? (
                    <span className="text-[#7b7b7b]">Pending</span>
                  ) : (
                    <div className="text-green-400 flex justify-end items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openProvablyFairDetails(bet)}
                        disabled={fairnessLoadingId !== null}
                        className="disabled:opacity-50"
                        aria-label={`Verify round ${bet.roundId}`}
                        title="Verify Provably Fair result"
                      >
                        <img
                          src="/icon-provabyfair.svg"
                          alt=""
                          className={`w-4 h-4 ${
                            fairnessLoadingId === bet.sessionId
                              ? "animate-pulse"
                              : ""
                          }`}
                        />
                      </button>
                      <img src="/share.svg" alt="Details" className="w-4 h-4" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {visibleCount < sessionBetHistory.length && (
            <div className="px-6 py-4 border-t border-[#2c2d30] bg-[#1b1c1d]">
              <button
                className="w-full bg-[#2c2d30] text-gray-400 text-sm py-2 rounded-full hover:bg-[#3a3b3c] transition"
                onClick={() => setVisibleCount((previous) => previous + 10)}
              >
                Load more
              </button>
            </div>
          )}

          {fairnessError && (
            <div className="px-4 py-3 text-center text-xs text-red-400 border-t border-[#2c2d30]">
              {fairnessError}
            </div>
          )}
        </div>
      </div>
      </div>

      {selectedRound && (
        <ProvablyFairDetails
          isOpen={true}
          onClose={() => setSelectedRound(null)}
          roundData={selectedRound}
        />
      )}
    </>
  );
};

export default SessionBetHistoryModal;
